import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Calculator, 
  Clock, 
  ArrowRight, 
  Ruler,
  AlertTriangle,
  FileText,
  Layers,
  Target,
  TrendingUp,
  Gauge,
  Lightbulb,
  Send
} from 'lucide-react';

const CalculsIndex = () => {
  // Liste des calculateurs publiés
  const publishedCalculateurs = [
    {
      id: 'portance-micropieu',
      slug: '/calcul/portance-micropieu',
      title: 'Estimateur de portance d\'un micropieu',
      subtitle: 'Méthode pressiométrique',
      description: 'Pré-estimation pédagogique de la capacité portante d\'un micropieu selon les principes de l\'Eurocode 7 et de la norme NF P 94-262.',
      useTime: '2 min',
      icon: Ruler,
      color: 'bg-violet-500',
      tags: ['Micropieux', 'Eurocode 7', 'Frottement latéral', 'NF P 94-262'],
      published: true,
    },
    {
      id: 'estimer-ma-foreuse',
      slug: '/calcul/estimer-ma-foreuse',
      title: 'Estimer la valeur de ma foreuse',
      subtitle: 'Décote sur prix neuf',
      description: 'Estimation de la valeur de revente d\'une foreuse géotechnique basée sur l\'âge, les heures de forage et l\'état d\'entretien.',
      useTime: '3 min',
      icon: Gauge,
      color: 'bg-orange-500',
      tags: ['Foreuse', 'Occasion', 'Revente', 'Valorisation'],
      published: true,
    },
    {
      id: 'prix-etude-de-sol',
      slug: '/calcul/prix-etude-de-sol',
      title: 'Prix d\'une étude de sol',
      subtitle: 'Missions G1 à G5',
      description: 'Estimation du prix d\'une étude géotechnique selon le type de mission, la surface du projet et la complexité du terrain.',
      useTime: '2 min',
      icon: FileText,
      color: 'bg-cyan-500',
      tags: ['Étude de sol', 'G1', 'G2', 'Loi ELAN', 'Prix'],
      published: true,
    },
  ];

  // Calculateurs à venir
  const upcomingCalculateurs = [
    {
      id: 'tassement-semelle',
      title: 'Estimateur de tassement d\'une semelle',
      description: 'Calcul indicatif du tassement d\'une fondation superficielle selon la méthode pressiométrique.',
      icon: Layers,
      color: 'bg-blue-500',
    },
    {
      id: 'capacite-pieu',
      title: 'Capacité portante d\'un pieu foré',
      description: 'Estimation de la portance d\'un pieu de grand diamètre (frottement + pointe).',
      icon: Target,
      color: 'bg-green-500',
    },
    {
      id: 'stabilite-talus',
      title: 'Vérification simplifiée de talus',
      description: 'Pré-dimensionnement de la stabilité d\'un talus par la méthode des tranches.',
      icon: TrendingUp,
      color: 'bg-orange-500',
    },
  ];

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Outils de calcul géotechnique | GEOTS Consulting",
    "description": "Outils de calcul et d'estimation pour la géotechnique : portance de micropieux, tassement, stabilité. À usage pédagogique.",
    "url": "https://geots.consulting/calcul",
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    }
  };

  return (
    <>
      <Helmet>
        <title>Outils de calcul géotechnique | Estimations & pré-dimensionnement | GEOTS</title>
        <meta name="description" content="Outils de calcul géotechnique gratuits : estimateurs de portance, pré-dimensionnement de fondations, calculs indicatifs. À usage pédagogique, ne remplacent pas une étude." />
        <link rel="canonical" href="https://geots.consulting/calcul" />
        <meta property="og:title" content="Outils de calcul géotechnique | GEOTS Consulting" />
        <meta property="og:description" content="Outils de calcul géotechnique gratuits pour professionnels et étudiants." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://geots.consulting/calcul" />
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-[#635BFF] p-3 rounded-xl">
                  <Calculator className="w-8 h-8" />
                </div>
                <span className="text-[#00D4FF] font-semibold">Outils interactifs</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Calculs & Estimations
                <span className="text-[#00D4FF]"> Géotechniques</span>
              </h1>
              <p className="text-xl text-gray-300 mb-6">
                Des outils de pré-dimensionnement et d'estimation pour vos projets de fondations. 
                Gratuits, pédagogiques, à usage indicatif.
              </p>
              
              {/* Disclaimer */}
              <div className="bg-amber-500/20 border border-amber-400/30 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-amber-100 text-sm">
                  <strong>Important :</strong> Ces outils fournissent des ordres de grandeur indicatifs. 
                  Tout dimensionnement définitif doit être validé par un bureau d'études géotechniques qualifié.
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          {/* Published Calculators */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <Calculator className="w-6 h-6 text-[#635BFF]" />
              <h2 className="text-2xl font-bold text-[#0A2540]">Outils disponibles</h2>
              <span className="bg-[#635BFF] text-white text-xs font-bold px-2 py-1 rounded-full">
                {publishedCalculateurs.length}
              </span>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {publishedCalculateurs.map((calc) => (
                <Link
                  key={calc.id}
                  to={calc.slug}
                  className="group bg-white rounded-2xl border hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className={`${calc.color} p-6 text-white relative overflow-hidden`}>
                    <div className="absolute top-0 right-0 opacity-10">
                      <calc.icon className="w-32 h-32 -mr-8 -mt-8" />
                    </div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="bg-white/20 text-white text-xs font-bold px-2 py-1 rounded">
                          OUTIL INTERACTIF
                        </span>
                        <span className="flex items-center gap-1 text-sm text-white/80">
                          <Clock className="w-4 h-4" />
                          {calc.useTime}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-1">{calc.title}</h3>
                      <p className="text-white/80 text-sm">{calc.subtitle}</p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6">
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {calc.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {calc.tags.map((tag, i) => (
                        <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center text-[#635BFF] font-semibold group-hover:gap-3 transition-all">
                      Utiliser l'outil
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Upcoming Calculators */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <Clock className="w-6 h-6 text-gray-400" />
              <h2 className="text-2xl font-bold text-[#0A2540]">Prochainement</h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingCalculateurs.map((calc) => (
                <div
                  key={calc.id}
                  className="bg-white rounded-2xl border p-6 opacity-75 relative overflow-hidden"
                >
                  <div className="absolute top-4 right-4">
                    <span className="bg-gray-100 text-gray-500 text-xs font-bold px-2 py-1 rounded">
                      BIENTÔT
                    </span>
                  </div>
                  <div className={`${calc.color} w-12 h-12 rounded-xl flex items-center justify-center mb-4 opacity-50`}>
                    <calc.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0A2540] mb-2">{calc.title}</h3>
                  <p className="text-gray-500 text-sm">{calc.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Suggest a Calculator */}
          <section className="mb-16">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="bg-amber-500 p-4 rounded-xl">
                  <Lightbulb className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#0A2540] mb-2">
                    Vous avez une idée de calculateur ?
                  </h3>
                  <p className="text-gray-600">
                    Suggérez-nous un outil de calcul qui vous serait utile dans votre pratique quotidienne. 
                    Nous développons en priorité les outils les plus demandés par la communauté.
                  </p>
                </div>
                <a
                  href="mailto:contact@geots.consulting?subject=Suggestion%20de%20calculateur%20géotechnique&body=Bonjour,%0A%0AJe%20souhaite%20suggérer%20un%20nouveau%20calculateur%20:%0A%0ANom%20du%20calcul%20:%20%0A%0ADescription%20:%20%0A%0AContexte%20d'utilisation%20:%20%0A%0AMerci%20!"
                  className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <Send className="w-5 h-5" />
                  Suggérer un calcul
                </a>
              </div>
            </div>
          </section>

          {/* Link to Guides */}
          <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 md:p-12 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  Besoin de comprendre la théorie ?
                </h2>
                <p className="text-white/80 max-w-xl">
                  Nos guides techniques détaillent les méthodes de calcul, les normes applicables 
                  et les bonnes pratiques en géotechnique.
                </p>
              </div>
              <Link 
                to="/guides" 
                className="bg-white text-[#635BFF] px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap"
              >
                <FileText className="w-5 h-5" />
                Voir les guides
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default CalculsIndex;
